import type {
  KitTopic,
  ScorecardRating,
  ScorecardRecommendation,
  SubmitScorecardData,
} from '../hooks/use-interview-kits'

export const SCORECARD_RECOMMENDATIONS: { value: ScorecardRecommendation; label: string }[] = [
  { value: 'advance', label: 'Advance' },
  { value: 'hold', label: 'Hold' },
  { value: 'reject', label: 'Reject' },
]

export type ScorecardDraftRating = Omit<ScorecardRating, 'level'> & { level: number | null }

export type ScorecardDraft = {
  recommendation: ScorecardRecommendation | null
  summary: string
  ratings: ScorecardDraftRating[]
}

/** One unrated row per kit topic, or the interviewer's previous ratings when re-scoring. */
export function initialScorecardDraft(
  topics: KitTopic[],
  previous?: { recommendation: ScorecardRecommendation; summary: string; ratings: ScorecardRating[] } | null,
): ScorecardDraft {
  const prior = new Map((previous?.ratings ?? []).map((r) => [r.competency_id ?? r.topic, r]))
  return {
    recommendation: previous?.recommendation ?? null,
    summary: previous?.summary ?? '',
    ratings: topics.map((t) => {
      const before = prior.get(t.competency_id ?? t.topic)
      return {
        topic: t.topic,
        competency_id: t.competency_id,
        target_level: t.target_level,
        level: before?.level ?? null,
        notes: before?.notes ?? '',
      }
    }),
  }
}

/** First problem that blocks submitting, or null when the draft is complete. */
export function scorecardDraftError(draft: ScorecardDraft): string | null {
  const unrated = draft.ratings.find((r) => r.level == null)
  if (unrated) return `Rate ${unrated.topic}.`
  if (!draft.recommendation) return 'Choose a recommendation.'
  return null
}

export function scorecardPayload(
  draft: ScorecardDraft,
  interviewId?: string | null,
): SubmitScorecardData {
  if (!draft.recommendation) throw new Error('Choose a recommendation.')
  return {
    recommendation: draft.recommendation,
    summary: draft.summary.trim(),
    interview_id: interviewId ?? null,
    ratings: draft.ratings.map((r) => ({
      topic: r.topic,
      competency_id: r.competency_id,
      target_level: r.target_level,
      level: r.level ?? 0,
      notes: r.notes.trim(),
    })),
  }
}

/** "Meets 3 of 5 targets" style count; ratings without a target are left out. */
export function scorecardTargetsMet(ratings: { level: number | null; target_level: number | null }[]) {
  const targeted = ratings.filter((r) => r.target_level != null)
  const met = targeted.filter((r) => r.level != null && r.level >= (r.target_level ?? 0)).length
  return { met, total: targeted.length }
}
